import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';
const LOCAL_KEY = 'puzzle_video_cart';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

const emptyCart = () => ({
  items: [],
  pickup_date: null,
  pickup_time: '',
  return_date: null,
  return_time: '',
});

const readLocalCart = () => {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.items)) return parsed;
  } catch { /* ignore */ }
  return null;
};

// Merge two carts: stack quantities for same product_id (non kit/cross-sell)
const mergeCarts = (target, source) => {
  const out = { ...target, items: [...(target.items || [])] };
  for (const src of source.items || []) {
    // Kits and cross-sells are always appended as separate lines (unique semantics)
    if (src.is_kit || src.is_cross_sell) {
      out.items.push(src);
      continue;
    }
    const idx = out.items.findIndex(
      (it) => !it.is_kit && !it.is_cross_sell && it.product_id === src.product_id
    );
    if (idx >= 0) {
      out.items[idx] = { ...out.items[idx], quantity: out.items[idx].quantity + src.quantity };
    } else {
      out.items.push(src);
    }
  }
  out.cpu_code = null; // invalidated by merge
  return out;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [cart, setCart] = useState(emptyCart());
  const [loading, setLoading] = useState(false);
  // Franchise buyback choice for vehicle rentals. Persisted in its own
  // localStorage key so it survives a page reload but is decoupled from the
  // synced cart payload (which we don't want to touch on the server).
  const FRANCHISE_KEY = 'pzv_franchise_choice';
  const [franchiseChoice, setFranchiseChoiceState] = useState(() => {
    try { return localStorage.getItem(FRANCHISE_KEY) || null; } catch { return null; }
  });
  const setFranchiseChoice = useCallback((choice) => {
    setFranchiseChoiceState(choice);
    try {
      if (choice) localStorage.setItem(FRANCHISE_KEY, choice);
      else localStorage.removeItem(FRANCHISE_KEY);
    } catch { /* ignore */ }
  }, []);
  // When user logs in with a non-empty local cart AND a non-empty server cart,
  // we present them with a "merge or replace?" modal. Components downstream
  // (e.g. a global modal mounted in App) read these values and call the handlers.
  const [pendingMerge, setPendingMerge] = useState(null); // { local, server } | null
  // null until auth is resolved on first mount. The very first transition
  // (loading -> resolved) MUST NOT trigger the merge popup, because the user
  // didn't actually just log in — they were already authenticated.
  const prevAuthRef = useRef(null);

  // Initial mount: load from local immediately, server later (via auth effect)
  useEffect(() => {
    const local = readLocalCart();
    if (local) setCart(local);
  }, []);

  const persistLocal = useCallback((c) => {
    try { localStorage.setItem(LOCAL_KEY, JSON.stringify(c)); } catch { /* ignore */ }
  }, []);

  const persistServer = useCallback(async (c) => {
    try {
      await fetch(`${API_BASE}/cart/`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(c),
      });
    } catch (err) { console.error('Failed to save cart:', err); }
  }, []);

  // Authentication state change → handle merge / load.
  // Wait until AuthContext finishes its initial check (authLoading=false) so
  // we don't mistake the first mount resolution for a real login event.
  useEffect(() => {
    if (authLoading) return; // wait for auth to settle
    const wasAuth = prevAuthRef.current;

    // First time we observe a resolved auth state: just snapshot it, do NOT
    // treat it as a login event (user was already authenticated, or already
    // anonymous, when they opened the page).
    if (wasAuth === null) {
      prevAuthRef.current = isAuthenticated;
      // If already logged in on first mount, silently refresh the cart from
      // the server (overwrites the localStorage view, no popup).
      if (isAuthenticated) {
        (async () => {
          try {
            const res = await fetch(`${API_BASE}/cart/`, { credentials: 'include' });
            if (res.ok) {
              const serverCart = await res.json();
              setCart(serverCart);
              persistLocal(serverCart);
            }
          } catch { /* ignore */ }
        })();
      }
      return;
    }

    prevAuthRef.current = isAuthenticated;

    // Genuine "just logged in" transition
    if (!wasAuth && isAuthenticated) {
      (async () => {
        setLoading(true);
        try {
          const res = await fetch(`${API_BASE}/cart/`, { credentials: 'include' });
          const serverCart = res.ok ? await res.json() : emptyCart();
          const localCart = readLocalCart() || emptyCart();
          const serverHas = (serverCart.items || []).length > 0;

          if (serverHas) {
            // Always ask the user how to handle their previous (server) cart.
            // We surface the choice regardless of whether the local cart has
            // items: that way the user can opt to add, replace, or wipe the
            // previous cart in one click.
            setPendingMerge({ server: serverCart, local: localCart });
            // Display the local cart in the background; the modal commits the
            // final decision.
            setCart(localCart);
            persistLocal(localCart);
          } else {
            // No previous cart on the server — just keep the local one and
            // sync it to the server.
            setCart(localCart);
            persistLocal(localCart);
            if ((localCart.items || []).length > 0) {
              await persistServer(localCart);
            }
          }
        } finally {
          setLoading(false);
        }
      })();
      return;
    }

    // Genuine "just logged out" transition — clear the cart entirely (local + state).
    if (wasAuth && !isAuthenticated) {
      try { localStorage.removeItem(LOCAL_KEY); } catch { /* ignore */ }
      setCart(emptyCart());
    }
  }, [isAuthenticated, authLoading, persistLocal, persistServer]);

  // User chose how to handle their previous (server) cart upon login.
  //   'add'        -> add previous cart items to the current cart (deduped by product)
  //   'replace'    -> replace the current cart with the previous one
  //   'discard'    -> discard the previous cart, keep only the current one
  const confirmMerge = useCallback(async (mode) => {
    if (!pendingMerge) return;
    const { server, local } = pendingMerge;
    let next;
    if (mode === 'add')         next = mergeCarts(local, server);
    else if (mode === 'replace') next = { ...server, cpu_code: null };
    else                          next = { ...local,  cpu_code: null }; // 'discard'
    setCart(next);
    persistLocal(next);
    if (isAuthenticated) await persistServer(next);
    setPendingMerge(null);
  }, [pendingMerge, isAuthenticated, persistLocal, persistServer]);

  const loadCart = useCallback(async () => {
    if (!isAuthenticated) {
      const local = readLocalCart();
      if (local) setCart(local);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/cart/`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCart(data);
        persistLocal(data);
      }
    } catch (error) {
      console.error('Failed to load cart:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, persistLocal]);

  // Generic save: always mirrors to localStorage + server if logged in
  const saveCart = useCallback(async (newCart) => {
    setCart(newCart);
    persistLocal(newCart);
    if (isAuthenticated) {
      await persistServer(newCart);
    }
  }, [isAuthenticated, persistLocal, persistServer]);

  const applyCartChange = useCallback((transform) => {
    let committed = null;
    setCart((current) => {
      const next = transform(current);
      committed = next;
      return next;
    });
    queueMicrotask(() => {
      if (!committed) return;
      persistLocal(committed);
      if (isAuthenticated) persistServer(committed);
    });
  }, [isAuthenticated, persistLocal, persistServer]);

  const addItem = useCallback((product, quantity = 1) => {
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    applyCartChange((current) => {
      const newCart = { ...current, items: [...current.items] };

      if (product.is_kit) {
        newCart.items.push({
          product_id: product.id,
          product_name: product.name,
          product_sku: product.sku || 'GRIP-KIT',
          quantity: qty,
          daily_price: product.daily_price,
          image_url: product.image_url,
          is_kit: true,
          kit_items: product.kit_items || [],
        });
        newCart.cpu_code = null;
        return newCart;
      }

      if (product.is_cross_sell) {
        const csIdx = newCart.items.findIndex((item) =>
          item.is_cross_sell &&
          item.product_id === product.id &&
          (item.parent_product_id || null) === (product.parent_product_id || null)
        );
        if (csIdx >= 0) {
          newCart.items[csIdx] = {
            ...newCart.items[csIdx],
            quantity: newCart.items[csIdx].quantity + qty,
          };
        } else {
          newCart.items.push({
            product_id: product.id,
            product_name: product.name,
            product_sku: product.sku,
            quantity: qty,
            daily_price: product.daily_price,
            image_url: product.image_url,
            is_cross_sell: true,
            original_price: product.original_price || product.daily_price,
            parent_product_id: product.parent_product_id || null,
          });
        }
        newCart.cpu_code = null;
        return newCart;
      }

      const existingIndex = newCart.items.findIndex(
        (item) => item.product_id === product.id && !item.is_cross_sell
      );

      if (existingIndex >= 0) {
        newCart.items[existingIndex] = {
          ...newCart.items[existingIndex],
          quantity: newCart.items[existingIndex].quantity + qty,
        };
      } else {
        newCart.items.push({
          product_id: product.id,
          product_name: product.name,
          product_sku: product.sku,
          quantity: qty,
          daily_price: product.daily_price,
          image_url: product.image_url,
        });
      }

      newCart.cpu_code = null;
      return newCart;
    });
  }, [applyCartChange]);

  const removeItem = useCallback((productId) => {
    const newCart = { ...cart };
    newCart.items = newCart.items.filter((item) => item.product_id !== productId);
    newCart.cpu_code = null;
    saveCart(newCart);
  }, [cart, saveCart]);

  const updateQuantity = useCallback((productId, quantity) => {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }
    const newCart = { ...cart, items: [...cart.items] };
    const idx = newCart.items.findIndex((item) => item.product_id === productId);
    if (idx >= 0) {
      newCart.items[idx] = { ...newCart.items[idx], quantity };
      newCart.cpu_code = null;
      saveCart(newCart);
    }
  }, [cart, saveCart, removeItem]);

  const updateKit = useCallback((kitProductId, partialUpdate) => {
    const newCart = { ...cart, items: [...cart.items] };
    const idx = newCart.items.findIndex((item) => item.product_id === kitProductId && item.is_kit);
    if (idx < 0) return;
    const merged = { ...newCart.items[idx], ...partialUpdate };
    if (Array.isArray(merged.kit_items) && merged.kit_items.length === 0) {
      newCart.items.splice(idx, 1);
    } else {
      newCart.items[idx] = merged;
    }
    newCart.cpu_code = null;
    saveCart(newCart);
  }, [cart, saveCart]);

  const setDates = useCallback((pickupDate, pickupTime, returnDate, returnTime) => {
    const newCart = {
      ...cart,
      pickup_date: pickupDate,
      pickup_time: pickupTime,
      return_date: returnDate,
      return_time: returnTime,
    };
    saveCart(newCart);
  }, [cart, saveCart]);

  const clearCart = useCallback(() => {
    const e = emptyCart();
    setCart(e);
    localStorage.removeItem(LOCAL_KEY);
    setFranchiseChoice(null);
    if (isAuthenticated) {
      fetch(`${API_BASE}/cart/`, { method: 'DELETE', credentials: 'include' }).catch(console.error);
    }
  }, [isAuthenticated, setFranchiseChoice]);

  const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cart.items.reduce((sum, item) => sum + (item.daily_price * item.quantity), 0);

  const value = {
    cart,
    loading,
    addItem,
    removeItem,
    updateQuantity,
    updateKit,
    setDates,
    clearCart,
    loadCart,
    itemCount,
    subtotal,
    // Cart merge popup
    pendingMerge,
    confirmMerge,
    // Vehicle franchise buyback (persisted separately from the synced cart)
    franchiseChoice,
    setFranchiseChoice,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
